/**
 * The co-pilot's checklist editor: what we want to learn from every sales call.
 *
 * Its own file rather than more of `PilotCard`, because it is a different thing
 * from the switch above it. That card answers "does the co-pilot run and what
 * may it cost"; this list is the sales script itself, and it is data on purpose -
 * written into the prompt, changing a question would be a deploy, and the person
 * who knows which one was missing has just put the phone down.
 *
 * Rewording keeps the id, which is what `pilotState.answered` stores, so the
 * calls in flight keep their answered marks.
 *
 * Laid out as a TABLE rather than as a stack of cards. Twenty questions are
 * allowed here, and the previous shape - a sunken card per row, the question on
 * one line and the reason under it - spent 120px on each of them, so the six
 * default entries filled a screen on their own. A row per question is 36px, and
 * the header that becomes possible at that size is what finally says out loud
 * what the number on the right of each row means.
 */
import { Plus, Trash2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { PilotChecklistItem } from "../../../../lib/callPilot";
import { Btn, IconBtn } from "../../../ui";
import { FieldGroup, Note, Pill } from "../panel/fields";

/** Matches the server's cap in `crm-calls/lib/pilot/checklist.ts`. */
export const MAX_ITEMS = 20;

/**
 * One grid template for the header and for every row.
 *
 * Written once because two copies drift on the first edit and the columns then
 * stop lining up - which is exactly the failure a header row is supposed to fix.
 *
 * The four-column form is a CONTAINER query, not a screen one. What decides
 * whether a question and its reason can sit side by side is how wide this panel
 * is, and that is not the window: Settings spends a 214px menu and the app spends
 * a sidebar before this card gets any width at all, so the same 1280px laptop
 * gives it around 700px here and over 1000px with the sidebar collapsed. Below
 * the threshold the question takes a line of its own and the reason, the weight
 * and the bin share the next one - still half the height of the card-per-row
 * shape this replaced, and without truncating both fields to make it fit.
 */
const COLS =
  "grid grid-cols-[minmax(0,1fr)_66px_34px] gap-x-2 gap-y-1 @[820px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_66px_34px]";

export type Draft = PilotChecklistItem & { key: string };

let draftSeq = 0;

export function toDraft(item: PilotChecklistItem): Draft {
  // The key is local and never sent: a brand new row has no id yet, and keying
  // React on the id would make every unsaved row the same row.
  return { ...item, key: `d${(draftSeq += 1)}` };
}

export function PilotChecklist({
  items,
  onChange,
}: {
  items: Draft[];
  onChange: (next: (prev: Draft[]) => Draft[]) => void;
}) {
  const t = useT();

  function patch(key: string, change: Partial<PilotChecklistItem>) {
    onChange((prev) => prev.map((item) => (item.key === key ? { ...item, ...change } : item)));
  }

  return (
    <FieldGroup title={t("settings.pilot.checklist")}>
      <Note tone="mut">{t("settings.pilot.checklistHint")}</Note>

      <div className="@container space-y-1.5">
        {/* Shown only where all four columns really are side by side. On the
            stacked form the question owns a line of its own and these labels
            would name the wrong things - the weight carries its own "/10"
            instead. There is deliberately no drag handle: the server sorts the
            list by weight before it ever reaches the prompt, so the grip that
            used to sit here promised an ordering that changed nothing.

            The gutter lives in the cells rather than on this grid: padding on
            the header itself would narrow its tracks, and every label would sit
            a few pixels left of the column it names. */}
        <div
          className={`${COLS} hidden text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground @[820px]:grid`}
          aria-hidden="true"
        >
          <span className="px-4">{t("settings.pilot.colQuestion")}</span>
          <span className="px-4">{t("settings.pilot.colWhy")}</span>
          <span className="text-center">{t("settings.pilot.colWeight")}</span>
          <span />
        </div>

        {/* Stacked, a question and its reason are two lines that have to read as
            ONE entry, so the space between entries has to beat the space inside
            one. Back on the four-column form each entry is a single line and the
            list can close right up again. */}
        <ul className="flex flex-col gap-3 @[820px]:gap-1.5">
          {items.map((item) => (
            <li key={item.key} className={`${COLS} items-center`}>
              <Pill
                className="col-span-3 h-9 px-4 @[820px]:col-span-1"
                value={item.question}
                onChange={(v) => patch(item.key, { question: v })}
                placeholder={t("settings.pilot.questionPlaceholder")}
                ariaLabel={t("settings.pilot.colQuestion")}
              />
              <Pill
                className="h-9 px-4"
                value={item.why}
                onChange={(v) => patch(item.key, { why: v })}
                placeholder={t("settings.pilot.whyPlaceholder")}
                ariaLabel={t("settings.pilot.colWhy")}
              />
              {/* The scale is spelled out inside the field. A lone "9" in a
                  narrow box is the one thing on this page nobody could read off
                  the screen, and the header that explains it is not there on the
                  stacked form. */}
              <Pill
                className="h-9 gap-1 px-2.5"
                value={String(item.weight)}
                onChange={(v) => patch(item.key, { weight: Number(v) || 1 })}
                inputMode="numeric"
                centered
                placeholder="5"
                ariaLabel={t("settings.pilot.colWeight")}
                trailing={<span className="text-[11px] text-muted-foreground">/10</span>}
              />
              <IconBtn
                small
                onClick={() => onChange((prev) => prev.filter((row) => row.key !== item.key))}
                aria-label={t("settings.pilot.remove")}
                title={t("settings.pilot.remove")}
              >
                <Trash2 className="size-3.5" />
              </IconBtn>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Btn
          small
          onClick={() =>
            onChange((prev) =>
              prev.length >= MAX_ITEMS ? prev : [...prev, toDraft({ id: "", question: "", why: "", weight: 5 })],
            )
          }
          disabled={items.length >= MAX_ITEMS}
        >
          <Plus className="size-3.5" />
          {t("settings.pilot.addQuestion")}
        </Btn>
        {/* The cap said out loud. A disabled button with no number beside it
            reads as a broken button. */}
        <span className="text-[11.5px] text-muted-foreground">
          {t("settings.pilot.count", { count: items.length, max: MAX_ITEMS })}
        </span>
      </div>
    </FieldGroup>
  );
}
